import { useCallback, useMemo } from 'react';
import { AuthorizationService, HttpService } from '@bentley/pw-api';
import { useBuddi } from '@bentley/pw-config';
import { assertHttpServiceConfigured, handleBadResponse, handleDuplicateConnectionName } from '../../errors/errors';
import { t } from '../../translate';
import { toConnection, toPrdssSetting } from './conversion';
/**
 * ConnectionApi for engaging with ProjectWise WebView Datasource Connections.
 * @param datasourceConnectionsConfig Configuration for the Datasource Connections.
 * @param datasourceConnectionsConfig.buddiRegionCode Region code for BUDDI
 * @param datasourceConnectionsConfig.gprId Organization ID
 * @param datasourceConnectionsConfig.userOrg User organization from OIDC profile
 * @param datasourceConnectionsConfig.getOidcToken Async function to get OIDC token
 * @returns ConnectionApi
 */
export function useDatasourceConnections({ buddiRegionCode, gprId, userOrg, getOidcToken }) {
    const productSettingsUrl = useBuddi('NewProductSettingsService.Url', buddiRegionCode);
    const httpService = useMemo(() => {
        if (!productSettingsUrl) {
            return undefined;
        }
        const partition = userOrg ? 'Org' : 'User';
        return new HttpService({
            authorization: new AuthorizationService({ getOidcToken }),
            baseUrl: `${productSettingsUrl}Application/${gprId}/${partition}/Setting`
        });
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * getOidcToken should not re-trigger
         */
    }, [gprId, productSettingsUrl, userOrg]);
    const getAll = useCallback(async () => {
        assertHttpServiceConfigured(httpService);
        const prdssSettings = [];
        let continuationToken = '';
        do {
            let headers = {};
            if (continuationToken) {
                headers = { ContinuationToken: continuationToken };
            }
            try {
                const response = await httpService.get("?$filter=namespace eq 'datasources'", { headers });
                handleBadResponse(response);
                const data = (await response.json());
                const validConnections = data.filter((connection) => connection.properties.Id &&
                    connection.properties.Name &&
                    (connection.properties.Url || connection.properties.ConnectionUrl));
                prdssSettings.push(...validConnections);
                continuationToken = response.headers.get('ContinuationToken');
            }
            catch (e) {
                console.error(e);
                throw new Error(t('connections.failedToLoad'));
            }
        } while (continuationToken);
        const connections = prdssSettings.map(toConnection);
        return connections;
    }, [httpService]);
    const addOne = useCallback(async (connection) => {
        assertHttpServiceConfigured(httpService);
        const connections = await getAll();
        handleDuplicateConnectionName(connections, connection);
        const body = JSON.stringify(toPrdssSetting(connection));
        const response = await httpService.post('', body);
        handleBadResponse(response);
        const prdssSetting = (await response.json());
        return toConnection(prdssSetting);
    }, [getAll, httpService]);
    const updateOne = useCallback(async (connection) => {
        assertHttpServiceConfigured(httpService);
        const url = `datasources/${connection.Id}`;
        const body = JSON.stringify(toPrdssSetting(connection));
        const response = await httpService.put(url, body);
        handleBadResponse(response);
        const prdssSetting = (await response.json());
        return toConnection(prdssSetting);
    }, [httpService]);
    const deleteMany = useCallback(async (connectionIds) => {
        assertHttpServiceConfigured(httpService);
        for (const connectionId of connectionIds) {
            const url = `datasources/${connectionId}`;
            const response = await httpService.delete(url, null);
            handleBadResponse(response);
        }
    }, [httpService]);
    function invalidFunction() {
        throw new Error('Function not implemented for datasource connections');
    }
    const datasourceApi = useMemo(() => ({
        initialized: httpService != undefined,
        addOne,
        getAll,
        updateOne,
        upgradeOne: invalidFunction,
        deleteMany,
        addSynced: invalidFunction,
        getSynced: invalidFunction,
        deleteSynced: invalidFunction,
        addDriveSynced: invalidFunction,
        getDriveSynced: invalidFunction,
        setMultiMode: invalidFunction,
        deleteDriveSynced: invalidFunction,
        setPrimaryConnection: invalidFunction,
        unsetPrimaryConnection: invalidFunction,
        getPrimaryConnection: invalidFunction
    }), [addOne, deleteMany, getAll, httpService, updateOne]);
    return datasourceApi;
}
